/* eslint-disable react/display-name, react-hooks/exhaustive-deps */
import React, { createContext, useState, useEffect } from 'react';
import isEqual from 'lodash/isEqual';

// import config
import { getRegistrationConfig, getRegistrationLockedStatus } from '@utils/embeds/registration';

// import utils
import getDataForChannel, { getDataForChannelPreview } from '@utils/embeds/env/getDataForChannel';
// import getRegistrationLockedStatus from '@utils/embeds/registration/getRegistrationLockedStatus'

import deviceObservable from '@utils/dom/device';
import eventTracking, { generateTrackingValue, tealium } from '@utils/analytics/index';
import enterViewport from '@utils/dom/enterViewport';

import { createCustomEventObject } from '@utils/embeds/common/events';
import { loadRampart, loadCaaS } from '@utils/embeds/registration/caas';
import { loadThemeCSS } from '@embeds/core/themes';


import Root from '@embeds/common/Root/Root';

// Create Context
export const EnvironmentContext = createContext({});


const excludeToolsFromDefaultViewportTracking = [ 'poll', 'imageslideshow' ];

// TODO: whe we rolling out configuration of lock in CMS this function can be built out.
const isLockedOnRender = (data) => {
  console.log('isLockedOnRender', data.type);

  switch (data.type) {
  case 'toplist': {
    const locked = data.lockedConfig?.type !== 'limit'; // entire
    console.log('isLockedOnRender', locked);

    return locked;
  }
  // default case matches any of those type which are locked by default.
  default: return !data.type.match(/^(poll|quiz|personalityquiz|podcast|supervoter)$/);
  }
};

// Create context Provider
export const EnvironmentProvider = ({ children, initialProps, data, theme, handleHeightChange, previewParam, envRefreshTrigger, handlePreviewTabChange }) => {
  const { customColor } = data;
  // create state items
  const [ device, setDevice ] = useState(null);
  const [ envData, setEnvData ] = useState( (envRefreshTrigger ? getDataForChannelPreview(initialProps) : getDataForChannel(initialProps)) );
  const [ retryCount, setRetryCount ] = useState(0);
  const [ enteredViewport, setEnteredViewport ] = useState(false);

  // Get the registration config for current environment.
  const registrationConfig = getRegistrationConfig(envData);

  // determine if it should be registration locked for current widget/environment/site
  const [ registrationLocked, setRegistrationLocked ] = useState(getRegistrationLockedStatus(data, envData, registrationConfig));

  // Check for existing registration script dependencies
  const rampartGlobalObject = (window.Rampart !== undefined );
  const caasGlobalObject = (window.CaasLite !== undefined );

  // create state for dependencies
  const [ rampartLoaded, setRampartLoaded ] = useState( rampartGlobalObject );
  const [ caasLoaded, setCaasLoaded ] = useState( caasGlobalObject );
  const [ depsLoaded, setDepsLoaded ] = useState( (rampartLoaded && caasLoaded ) );

  const detailObject = { id: data.id, type: data.type };

  // create an event state for dependencies
  const [ caasLoadedEvent ] = useState( createCustomEventObject({
    name: 'et:registration:caas:loaded',
    el: document,
    detail: detailObject,
    handler: () => setCaasLoaded(true),
  }) );

  const [ rampartLoadedEvent ] = useState( createCustomEventObject({
    name: 'et:registration:rampart:loaded',
    el: document,
    detail: detailObject,
    handler: () => setRampartLoaded(true),
  }) );

  // if its registration locked, check if it should breach on initial render and if dependencies are loaded.
  const getCurrentLockedState = () => registrationLocked && depsLoaded && isLockedOnRender(data);

  // Current locked state
  const [ isLocked, setIsLocked ] = useState(getCurrentLockedState());

  const updateLockedStatus = () => {
    // registration locked status set to true if its locked on render.
    const isLockedState = getCurrentLockedState();

    if (isLockedState && !isLocked) {
      console.log('updateLockedStatus setIsLocked', data.type, data.id );
      setIsLocked(isLockedState);
    }
  };

  // function to re-get environment data.
  function _getEnvironmentContextData() {
    if (initialProps.isPreview) return console.log('_getEnvironmentContextData not run in preview mode');
    const newEnvData = getDataForChannel(initialProps);
    if (!isEqual(envData, newEnvData)) {
      console.log('_getEnvironmentContextData: envData and newEnvData not equal - update state', envData, newEnvData);
      setEnvData(newEnvData);
    } else {
      console.log('_getEnvironmentContextData: envData and newEnvData equal - do nothing', envData);
    }
    validateEnvData(newEnvData);
  }

  function _getEnvironmentContextDataPreview() {
    const newEnvDataPreview = getDataForChannelPreview(initialProps);
    if (!isEqual(envData, newEnvDataPreview)) setEnvData(newEnvDataPreview);
    setDevice(previewParam.deviceObj);
    validateEnvData(newEnvDataPreview);
  }

  // Function to validate Environment Data and to schedule a retry if needed.
  function validateEnvData(newEnvData) {
    const { site, userId, userAccessLevel } = newEnvData || {};
    const isValid = (site && userId && userAccessLevel);

    if (envData.isApp || window.location.search.match('debugEditorialTools')) {
      console.log('validateEnvData', newEnvData, data.type, data.id, initialProps.isPreview, isValid, site, userId, userAccessLevel, isValid, );
    }

    // if its valid, reset count and return true
    if (isValid) {

      if (envData.isApp) {
        // it is hard to debug env data on webviews as by the time u connect with devtools page is already loaded.
        // setting a timeout so that we can periodically see the envData in a webview.
        const time = (retryCount * 500) + 2000;
        setTimeout( () => setRetryCount((retryCount + 1)), time );
      } else {
        if (retryCount > 0) setRetryCount(0);
      }

      return true;
    }
    // if its preview dont schedule any retries coz it'll never exist.
    // if (initialProps.isPreview) return false;

    // schedule a retry if below threshold.
    if (retryCount < 5) {
      const time = (retryCount * 500) + 500;
      console.log('no site, userid or access data in env Data. lets set a timeout to check again. retries: ', retryCount, time);
      setTimeout( () => {
        console.log('timeout', retryCount, (retryCount + 1));
        setRetryCount((retryCount + 1));
      }, time );
    } else {
      console.log('hit the retry limit for fetching env data.', newEnvData);
    }

    return false;
  }

  // Function to ensure that CaaS is loaded.
  function ensureCaasScript() {
    // dispatch unlock event to tell self and other widgets on page that caas is loaded.
    if (!caasGlobalObject) return loadCaaS(registrationConfig, detailObject)
      .then( () => caasLoadedEvent.el.dispatchEvent(caasLoadedEvent.event) )
      .catch( () => setRegistrationLocked(false));
    if (!caasLoaded) caasLoadedEvent.el.dispatchEvent(caasLoadedEvent.event);
  }

  // Function to ensure that Rampart is loaded.
  function ensureRampartScript() {
    // dispatch unlock event to tell self and other widgets on page that Rampart is loaded.
    if (!rampartGlobalObject) return loadRampart(registrationConfig, detailObject)
      .then( () => rampartLoadedEvent.el.dispatchEvent(rampartLoadedEvent.event))
      .catch( () => setRegistrationLocked(false));
    if (!rampartLoaded) rampartLoadedEvent.el.dispatchEvent(rampartLoadedEvent.event);
  }

  // side effect for componentDidMount
  useEffect( () => {
    // Add event listeners to listen for registration Script dependency events fired by this embed or others on page.
    const scriptEvents = [ caasLoadedEvent, rampartLoadedEvent ];
    scriptEvents.forEach( ({ el, name, handler }) => el && el.addEventListener(name, handler) );

    // if its not web, load tealium
    if (envData.loadTealium) {
      window.utag_data = window.utag_data || {};
      window.utag_data.disable_pageview = true;
      tealium(envData);
    }

    // setup viewport helper for default tracking.
    const initDefaultViewportTracking = (initialProps.rootEl && !excludeToolsFromDefaultViewportTracking.includes(initialProps.type));

    enterViewport(data.type, initialProps.rootEl, () => {
      setEnteredViewport(true);
      // console.log('initial enter viewport', data.type)

      if (initDefaultViewportTracking) {
        eventTracking({
          eVar25: generateTrackingValue(data, 'viewport'),
          events: [ 'event158' ],
        }, envData);
      }
    });

    // Subscribe to the Observable device helper.
    deviceObservable.subscribe( ({ name }) => {
      if (initialProps.isPreview) return console.log('is Preview - no need to react to deviceObserver changes, as device is controlled by preview dropdown');
      setDevice(name);
    });

    return () => {
      // remove event listeners when unmounting.
      scriptEvents.forEach( ({ el, name, handler }) => el && el.removeEventListener(name, handler) );

      // remove device observable
      deviceObservable.unsubscribe();
    };
  }, []);

  // side effect to retry _getEnvironmentContextData on change of retryCount
  useEffect( () => {
    console.log('retryCount', retryCount);
    if (retryCount) _getEnvironmentContextData();
  }, [ retryCount ]);


  useEffect( () => {
    if (envRefreshTrigger) _getEnvironmentContextDataPreview();
  }, [ envRefreshTrigger ]);


  // Side effect for Environment Data change to determine locked state.
  useEffect( () => {
    const isValid = validateEnvData(envData);

    if (isValid) {
      // if envData has changed, lets recalculate the registration config to determine if its locked
      const registrationConfig = getRegistrationConfig(envData);
      const newRegLockedStatus = getRegistrationLockedStatus(data, envData, registrationConfig);
      // console.log('environment data has changed, lets check registration status', registrationConfig, newRegLockedStatus)
      setRegistrationLocked(newRegLockedStatus);
    }
  }, [ envData ] );

  // Side effect for rampart and caas script state to set a combined dependency state.
  useEffect( () => {
    const loaded = (rampartLoaded && caasLoaded );
    if (loaded) setDepsLoaded( loaded );
  }, [ rampartLoaded, caasLoaded ] );

  // Side Effect when registration locked state or dependencies loaded changes.
  useEffect( () => {
    // console.log(`registration locked side effect. Reg locked: ${registrationLocked}, Dependencies loaded: ${depsLoaded}`, data.id, data.type)
    if (!registrationLocked) return;

    if (!depsLoaded) {
      ensureRampartScript();
      ensureCaasScript();
      return;
    }

    updateLockedStatus();
  }, [ registrationLocked, depsLoaded ]);

  useEffect( () => {
    // console.log(`isLocked state change`, data.id, data.type, isLocked, envData)
  }, [ isLocked ]);


  useEffect( () => {
    const isSurveyComp = Boolean(data.type.match(/^(competition|survey)$/));
    const loadCSS = initialProps.isPreview || envData.isApp || envData.isAmp || isSurveyComp;
    console.log(`envContext: theme:${ theme }, isPreview: ${ initialProps.isPreview }, isApp: ${ envData.isApp }, isAmp: ${ envData.isAmp }, isSurveyComp: ${ isSurveyComp }`);
    if (loadCSS) loadThemeCSS(theme);
  }, [ theme ] );


  // create context provider value object container all states / handlers
  const value = {
    ...envData,
    theme,
    device,
    isLocked,
    enteredViewport,
    registration: {
      ...registrationConfig,
      registrationLocked,
      rampartLoaded,
      caasLoaded,
      depsLoaded,
      handleClickDisable: () => {
        console.log('handleClickDisable temp');
        setIsLocked(true);
      },
      handleComplete: () => {
        console.log('registration handle complete');
        setRegistrationLocked(false); // set to ensure any logic in tools to show overlay/reg form will fail.
        setIsLocked(false); // set current locked state to reenable functionality.
      },
      handleLock: () => setIsLocked(true),
      handleUnlock: () => setIsLocked(false)
    },
    // methods
    _getEnvironmentContextData,
    _updateMinHeight: handleHeightChange,
    _handlePreviewTabChange: handlePreviewTabChange
  };

  console.log('EnvironmentProvider', theme, value);

  return (
    <EnvironmentContext.Provider value={value}>
      <Root
        device={device}
        channel={envData.channel}
        theme={theme}
        customColor={customColor}
        // thememode={}
      >
        { children }
      </Root>
    </EnvironmentContext.Provider>
  );
};

export default EnvironmentProvider;


// export context helper hoc
export const withEnvironmentContext = Component => props => (
  <EnvironmentContext.Consumer>
    { (envData) => <Component {...props} envData={envData} device={envData.device || undefined} /> }
  </EnvironmentContext.Consumer>
);
